import { create } from 'zustand'

import {
  SPLITS_ACTIVE_ORG_COOKIE,
  SPLITS_AUTH_TOKEN_COOKIE,
  SPLITS_TOAST_MESSAGES_COOKIE,
} from '~/constants/cookies'

// Native cookie helpers to avoid adding a dependency
const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift()
  }
  return undefined
}

const setCookie = (
  name: string,
  value: string,
  options: { path: string; maxAge: number; secure: boolean; sameSite: string },
): void => {
  if (typeof document === 'undefined') return
  const cookieString = `${name}=${value}; path=${options.path}; max-age=${options.maxAge}; samesite=${options.sameSite}${options.secure ? '; secure' : ''}`
  document.cookie = cookieString
}

const removeCookie = (name: string, path: string): void => {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=; path=${path}; max-age=0`
}

interface CookieState {
  authToken: string | undefined
  activeOrgId: string | undefined
  toastMessages: string[] | undefined

  // Actions
  setAuthToken: (token: string) => void
  removeAuthToken: () => void
  setActiveOrgId: (orgId: string) => void
  removeActiveOrgId: () => void
  setToastMessages: (messages: string[]) => void
  removeToastMessages: () => void

  // Initialize from document.cookie (call once at app start)
  hydrate: () => void
}

export const useCookieStore = create<CookieState>((set) => ({
  authToken: undefined,
  activeOrgId: undefined,
  toastMessages: undefined,

  hydrate: () => {
    const toastMessagesRaw = getCookie(SPLITS_TOAST_MESSAGES_COOKIE.name)
    let toastMessages: string[] | undefined
    if (toastMessagesRaw) {
      try {
        toastMessages = JSON.parse(decodeURIComponent(toastMessagesRaw))
      } catch {
        toastMessages = undefined
      }
    }

    set({
      authToken: getCookie(SPLITS_AUTH_TOKEN_COOKIE.name),
      activeOrgId: getCookie(SPLITS_ACTIVE_ORG_COOKIE.name),
      toastMessages,
    })
  },

  setAuthToken: (token) => {
    setCookie(
      SPLITS_AUTH_TOKEN_COOKIE.name,
      token,
      SPLITS_AUTH_TOKEN_COOKIE.options,
    )
    set({ authToken: token })
  },

  removeAuthToken: () => {
    removeCookie(SPLITS_AUTH_TOKEN_COOKIE.name, '/')
    set({ authToken: undefined })
  },

  setActiveOrgId: (orgId) => {
    setCookie(
      SPLITS_ACTIVE_ORG_COOKIE.name,
      orgId,
      SPLITS_ACTIVE_ORG_COOKIE.options,
    )
    set({ activeOrgId: orgId })
  },

  removeActiveOrgId: () => {
    removeCookie(SPLITS_ACTIVE_ORG_COOKIE.name, '/')
    set({ activeOrgId: undefined })
  },

  setToastMessages: (messages) => {
    setCookie(
      SPLITS_TOAST_MESSAGES_COOKIE.name,
      encodeURIComponent(JSON.stringify(messages)),
      SPLITS_TOAST_MESSAGES_COOKIE.options,
    )
    set({ toastMessages: messages })
  },

  removeToastMessages: () => {
    removeCookie(SPLITS_TOAST_MESSAGES_COOKIE.name, '/')
    set({ toastMessages: undefined })
  },
}))

// Reads document.cookie directly, so it's correct even before hydrate() runs.
export const hasAuthCookie = (): boolean =>
  getCookie(SPLITS_AUTH_TOKEN_COOKIE.name) !== undefined

export const getAuthCookie = (): string | undefined =>
  getCookie(SPLITS_AUTH_TOKEN_COOKIE.name)
